//  Node imports
import { delayedCall, dispatcher, ingenuity, killDelayedCall } from "@quest/base-core";
import { IEvent } from "@quest/adapter-interface/dist";
import { MetersController, SlotEventConstantsBehavior, SoundConstant } from "@quest/murka-games-behavior-leopard/dist";
import { Model as platformModel, EventConstants as platformEventConstants } from "@quest/platform-murka/dist";
import { SlotEventConstants } from "@quest/slot-core";

//  Class imports
import { GameParserModel } from "../Platform/GameParserModel";
import { eventDispatchers } from "../Constants/EventDispatchers";
import { gameConstants } from "../Constants/gameConstants";
import { BaseGameModel } from "../model/BaseGameModel";
import { BaseGameViewGame } from "./BaseGameViewGame";
import { SoundConstantGame } from "../Constants/SoundConstantGame";


/**
 * MetersControllerGame class handles the meters for the base game
 *  ex. win meter, win tickup etc.
 */
export class MetersControllerGame extends MetersController {

  protected view: BaseGameViewGame;
  protected Model: BaseGameModel;

  //constructor
  constructor(view: BaseGameViewGame, Model: BaseGameModel) {
    super(view, Model);
    gameConstants.WIN_METER_OBJECT = this.winValueMeter;
    dispatcher.fireEvent(SlotEventConstantsBehavior.SET_BOTTOM_BAR_BET);
  }

  /**
   * subscribeEvents method overrided to subscribe game end events
   */
  protected subscribeEvents(): void {
    super.subscribeEvents();
    dispatcher.on(eventDispatchers.START_POT_WIN_TICKUP, this.startPotWinTickup, this);
    dispatcher.on(eventDispatchers.START_JP_BUTTON_PANEL_TICKUP, this.startJpButtonPanelTickup, this);
    dispatcher.on(eventDispatchers.FORCE_STOP_POT_WIN_METER_TICKUP, this.forceStopPotWinMeter, this);
  }

  /**
   * unsubscribeEvents method overrided to unSubscribe game end events
   */
  protected unsubscribeEvents(): void {
    super.unsubscribeEvents();
    dispatcher.off(eventDispatchers.START_POT_WIN_TICKUP, this.startPotWinTickup, this);
    dispatcher.off(eventDispatchers.START_JP_BUTTON_PANEL_TICKUP, this.startJpButtonPanelTickup, this);
    dispatcher.off(eventDispatchers.FORCE_STOP_POT_WIN_METER_TICKUP, this.forceStopPotWinMeter, this);
  }

  /**
   * onReturningFreegameUpdateMeter overrided to update the bottom bar win and
   * base win meter after returning from free game.
   * @param evt 
   */
  protected onReturningFreegameUpdateMeter(evt: IEvent): void {
    super.onReturningFreegameUpdateMeter(evt);
        dispatcher.fireEvent(SlotEventConstantsBehavior.SHOW_BASE_GAME_WIN_METER);

    dispatcher.fireEvent(platformEventConstants.UPDATE_BS_WIN, {
      value:
        ((ingenuity.parserModel as platformModel).getTotalWin()).toString()
    });
    (ingenuity.parserModel as GameParserModel).setIsFreeSpintriggered(false);
  }

  /*
  Override to update totalwin(getCurrentSpinTotalWinAmt()) during currentSpin
  */
  protected onStartWinTickUp(evt?: IEvent) {
    dispatcher.fireEvent(SlotEventConstantsBehavior.SHOW_BASE_GAME_WIN_METER);
    if(!(ingenuity.parserModel as GameParserModel).getIsFreeSpintriggered()){
      (ingenuity.parserModel as GameParserModel).setGameWinAmt((ingenuity.parserModel as GameParserModel).getCurrentSpinTotalWinAmt());
    } else if((ingenuity.parserModel as GameParserModel).getIsFreeSpintriggered() && !this.model.getIsBigWin() && (ingenuity.parserModel as GameParserModel).getHasLineWins()){
      (ingenuity.parserModel as GameParserModel).setGameWinAmt((ingenuity.parserModel as GameParserModel).getCurrentSpinTotalWinAmt() - (ingenuity.parserModel as GameParserModel).getFreeSpinTotalWin());
    }
    const currentWinAmt: number = (ingenuity.parserModel as GameParserModel).getGameWinAmt();
    this.winValueMeter && (this.winValueMeter.startTick(currentWinAmt, this.model.getIsCredits(), false, this.model.getWinMeterTickUpDuration(currentWinAmt), true, this.updateBalance.bind(this)));
    (ingenuity.soundManager as any).playSound(this.model.getWinMeterTickUpSound());
    if ((ingenuity.soundManager as any).getSoundInstance(SoundConstant.BASEGAME_BGMLOOP, "soundList")) {
      this.fadeBaseGameBGM();
    }
    this.fadeBaseGameReelSound();
  }

  protected updateBalance(): void {
    if (!this.model.getIsScatterWins()) {
      dispatcher.fireEvent(SlotEventConstants.UPDATE_BALANCE_METER);
    }
    if (ingenuity.soundManager.getSoundInstance(SoundConstant.BASEGAME_BGMLOOP, "soundList")) {
      ingenuity.soundManager.getSoundInstance(SoundConstant.BASEGAME_BGMLOOP, "soundList").volume = 1;
    }
    // To update the bottom bar win in case of basewin updating from tickup
    if((ingenuity.parserModel as GameParserModel).getIsFreeSpintriggered()){
      dispatcher.fireEvent(SlotEventConstantsBehavior.UPDATE_BOTTOM_BAR_WIN, { value: (ingenuity.parserModel as GameParserModel).getGameWinAmt() });
    } else {
      dispatcher.fireEvent(SlotEventConstantsBehavior.UPDATE_BOTTOM_BAR_WIN, { value: ingenuity.parserModel.getTotalWin() });
    }
    if (this.model.getIsWin()) {
        dispatcher.fireEvent(SlotEventConstantsBehavior.SEND_CLOSE_GAME_REQUEST);
    }
    this.unfadeBaseGameReelSound();
  }

  /*
  onForceStopWinTickUp method Override to play stop sounds
  */
  protected onForceStopWinTickUp(evt?: IEvent): void {
    super.onForceStopWinTickUp(evt);
    if ((ingenuity.soundManager as any).getSoundInstance(SoundConstant.BASEGAME_BGMLOOP, gameConstants.SOUND_LIST_PRELOAD)) {
      (ingenuity.soundManager as any).getSoundInstance(SoundConstant.BASEGAME_BGMLOOP, gameConstants.SOUND_LIST_PRELOAD).volume = 0;
    }
    this.unfadeBaseGameReelSound();
  }

  /*
  startPotWinTickup method start tickup to collect prize values from pot
  */
  private startPotWinTickup(): void {
    const numberOfPotsBG: number = this.getNumberOfPotsBG();
    (ingenuity.parserModel as GameParserModel).setGameWinAmt((ingenuity.parserModel as GameParserModel).getPrizePotCredit());

    const prizeCreditWonBG: any = this.getGameWinAmt();
    const tickUpDuration: number = numberOfPotsBG * 1.6;

    dispatcher.fireEvent(SlotEventConstantsBehavior.SHOW_BASE_GAME_WIN_METER);

    this.startWinValueMeter(prizeCreditWonBG, tickUpDuration);
    this.playWinTickupSound(prizeCreditWonBG);
}

/*
getNumberOfPotsBG method returns number of pots
*/
private getNumberOfPotsBG(): number {
    return (ingenuity.parserModel as GameParserModel).getNumberOfPotsBG();
}

/*
  getGameWinAmt method returns game win amount
  */
private getGameWinAmt(): any {
    return (ingenuity.parserModel as GameParserModel).getGameWinAmt();
}

/*
  startWinValueMeter method start tickup for win
  */
private startWinValueMeter(prizeCreditWonBG: any, tickUpDuration: number): void {
    if (this.winValueMeter) {
        this.winValueMeter.startTick(prizeCreditWonBG, this.model.getIsCredits(), false, tickUpDuration, true, () => {
          if (!(ingenuity.parserModel as GameParserModel).getIsJackpotWonBG() && !(ingenuity.parserModel as GameParserModel).getHasLineWins() && !this.model.getIsBigWin()) {
            this.updateBalance();
          }
          this.unfadeBaseGameBGM();
          this.unfadeBaseGameReelSound();
          dispatcher.fireEvent(SlotEventConstantsBehavior.DISABLE_STAGE_CLICK);
          ingenuity.configData.stageClickDuringPotConversion = false;
          dispatcher.fireEvent(SlotEventConstants.SHOW_NEXT_WIN_PRESENTATION);
          ingenuity.soundManager.stop(this.model.getWinMeterTickUpSound(prizeCreditWonBG as any));
          ingenuity.configData.isForceStopDuringPot=false;
        });
    }
}

/*
  playWinTickupSound method handle souns
  */
private playWinTickupSound(prizeCreditWonBG: any): void {
    (ingenuity.soundManager as any).playSound(this.model.getWinMeterTickUpSound(prizeCreditWonBG as any), 1, false);

    if (ingenuity.soundManager.getSoundInstance(SoundConstant.BASEGAME_BGMLOOP, "soundList")) {
        this.fadeBaseGameBGM();
    }
    this.fadeBaseGameReelSound();
}

  /*
  forceStopPotWinMeter method to stop tickup and directly show value on meter
  */
  private forceStopPotWinMeter(): void {
    (ingenuity.parserModel as GameParserModel).setGameWinAmt((ingenuity.parserModel as GameParserModel).getPrizePotCredit());
    const prizeCreditWonBG: any = (ingenuity.parserModel as GameParserModel).getGameWinAmt();
    const delayTimeDuration:number = 1500;
    if (ingenuity.configData.isForceStopDuringPot) {
      dispatcher.fireEvent(SlotEventConstants.DISABLED_ALL_BUTTONS);
      ingenuity.soundManager.stop(this.model.getWinMeterTickUpSound());
      if(!this.winValueMeter.getIsTickUpRunning()){
        this.winValueMeter.visible=true;
        this.winValueMeter.setCurrencyFormattedValue(prizeCreditWonBG);
        delayedCall("onComplete",delayTimeDuration,()=>{
          killDelayedCall("onComplete")
          this.presentationAfterOnComplete(prizeCreditWonBG);
        })
      }
      else{
        this.winValueMeter.forceStop(prizeCreditWonBG, false, true, delayTimeDuration, () => {
          this.presentationAfterOnComplete(prizeCreditWonBG);
        });
      }
      
    }
  }
  
  /*
  presentationAfterOnComplete method to show next presentation
  */
  private presentationAfterOnComplete(prizeCreditWonBG:number):void{
    ingenuity.configData.isForceStopDuringPot=false;
    if (!(ingenuity.parserModel as GameParserModel).getIsJackpotWonBG() && !(ingenuity.parserModel as GameParserModel).getHasLineWins() && !this.model.getIsBigWin()) {
      this.updateBalance();
    }
    this.unfadeBaseGameBGM();
    this.unfadeBaseGameReelSound();
    dispatcher.fireEvent(SlotEventConstantsBehavior.DISABLE_STAGE_CLICK);
    ingenuity.configData.stageClickDuringPotConversion = false;
    dispatcher.fireEvent(SlotEventConstants.SHOW_NEXT_WIN_PRESENTATION);
    ingenuity.soundManager.stop(this.model.getWinMeterTickUpSound(prizeCreditWonBG as any));
  }

  /*
  presentationAfterOnComplete method to start tickup value during jackpot banners
  */
  private startJpButtonPanelTickup(data?: IEvent): void {
    const tickupEndValue: any = data.data;
    const previousMeterValue: number = this.winValueMeter.getValue();
    (ingenuity.parserModel as GameParserModel).setGameWinAmt(tickupEndValue + previousMeterValue);
    const tickupValue: number = (ingenuity.parserModel as GameParserModel).getGameWinAmt();
    dispatcher.fireEvent(SlotEventConstantsBehavior.SHOW_BASE_GAME_WIN_METER);
    this.winValueMeter && (this.winValueMeter.startTick(tickupValue, this.model.getIsCredits(), false, 5, true, () => {
      if (!(ingenuity.parserModel as GameParserModel).getHasLineWins() && !this.model.getIsBigWin()) {
        this.updateBalance();
      }
      this.unfadeBaseGameBGM();
      this.unfadeBaseGameReelSound();
    }));
    if (ingenuity.soundManager.getSoundInstance(SoundConstant.BASEGAME_BGMLOOP, "soundList")) {
      this.fadeBaseGameBGM();
    }
    this.fadeBaseGameReelSound();
  }

  /*
  fadeBaseGameReelSound method fade music spin sound
  */
  private fadeBaseGameReelSound(): void {
    if ((ingenuity.soundManager as any).getSoundInstance(SoundConstantGame.MUSIC_SPIN, "soundList")) {
      ingenuity.soundManager.getSoundInstance(SoundConstantGame.MUSIC_SPIN, "soundList").volume = 0.3;
    }
  }

  /*
  unfadeBaseGameReelSound method to unfade music spin sound
  */
  private unfadeBaseGameReelSound(): void {
    if ((ingenuity.soundManager as any).getSoundInstance(SoundConstantGame.MUSIC_SPIN, "soundList")) {
      ingenuity.soundManager.getSoundInstance(SoundConstantGame.MUSIC_SPIN, "soundList").volume = 1;
    }
  }

  /*
  unfadeBaseGameReelSound method to unfade BGM sound
  */
  private unfadeBaseGameBGM() {
    if (ingenuity.soundManager.getSoundInstance(SoundConstant.BASEGAME_BGMLOOP, "soundList")) {
      ingenuity.soundManager.getSoundInstance(SoundConstant.BASEGAME_BGMLOOP, "soundList").volume = 1;
    }
  }
}
